import { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { controlToastState, openModal, SpinningLoader } from "risa-oasis-ui_v2";
import { FirestoreService } from "../../../../api/firebase/firestoreService";
import { FirestoreCollectionReference } from "../../../../api/firebase/references";
import { commentRpaModalSave } from "../../../../api/postCall/commentRpaModalSave";
import { getNccnGuideLines } from "../../../../api/postCall/nccnGuidelines";
import BadgeWithIcon from "../../../../components/badgeWithIcon/badgeWithIcon";
import BadgeWithIconRounded from "../../../../components/badgeWithIconRounded/badgeWithIconRounded";
import ClinicalDataTable from "../../../../components/modals/clinicalDataTable/ClinicalDataTable";
import CommentRpaNewModal from "../../../../components/modals/commentRPAnewModal/commentRpaNewModal";
import FormSelectionModal from "../../../../components/modals/formSelectionModal/formSelectionModal";
import HappyFlowModal from "../../../../components/modals/happyFlowModal/happyFlowModal";
import NarRpaTriggerModal from "../../../../components/modals/narRPATrigger/narRpaTriggerModal";
import TagWithIcon from "../../../../components/tagWithIcon/tagWithIcon";
import { useBusniessOfficeNotesContext } from "../../../../context/busniessOfficeNotesContext";
import { useInitialDataOfSingleMedicalOrder } from "../../../../context/initialDataOfSingleMedicalOrder";
import { usePayerNameContextFromSingleOrderLayout } from "../../../../context/payerNameContextFromSingleOrderLayout";
import { BadgeWithIconProps } from "../../../../data-model/badgeWithIconProps";
import { DocumentsViewerPropsWithSideNavigation } from "../../../../data-model/documentModalWithSideBav";
import { MedicalPaOrder } from "../../../../data-model/medicalPaOrdersModel";
import { NarRpsStatus } from "../../../../enums/evBvWriteBackStatus";
import {
  ModalNamesForBigQuery,
  ScreenNamesForBigQuery,
} from "../../../../enums/eventsData";
import { EventsName } from "../../../../enums/eventsName";
import { LocalStorageKeys } from "../../../../enums/localStorageKeys";
import { MedicalPaOrdersAuthStatus } from "../../../../enums/medicalPaOrdersAuthStatus";
import { ModalId } from "../../../../enums/modalId";
import { useLogJCodeStatusEvents } from "../../../../hooks/logJCodeStatusEvents";
import { useHealthCareFacility } from "../../../../hooks/useHealthCareFacility";
import {
  closeModal,
  setOpenedModalId,
} from "../../../../redux/slice/modalSlice";
import { AppDispatch, RootState } from "../../../../redux/store/store";
import { AlertTriangleIcon } from "../../../../svg/alertTriangle";
import { RedFlagIcon } from "../../../../svg/redFlag";
import RefreshIcon from "../../../../svg/refresh-icon";
import WarningIcon from "../../../../svg/warningIcon";
import { usePrimaryIcdCodes } from "../../../../utils/getPrimaryDiagnosisCodes";
import { getItemFromLocalStorage } from "../../../../utils/localStorageHelper";
import { getOrgIdForFetchExternalWorklist } from "../../../../utils/organizationHelper";
import { apiWorkerManager } from "../../../../workerManagers/workerManager";
import ThreeDotsWithClickableOptions from "../../../medicalPaForm/components/threeDotsWithClickableOptions";
import { handleEditIcd } from "../../actionHandlers/authorizationDetailsActionHandler";
import { useAuthOnFileRefresh } from "../../hooks/useAuthOnFileRefresh";
import { getAllJCodes } from "../../utils/existingDocsToLs";
import { formatJcodeIcd } from "../../utils/getJCode";
import { getBadgeDetailsAndIconColorForCareTag } from "../busniessOffice/utils/getBadgeAndIconColorForCareTag";
import AuthorizationDetails from "./components/authorizationDetails";
interface AuthHeaderProps {
  authorizationId?: string;
  patientName?: string;
  patientMrn?: string;
  location?: string;
  status?: string;
  orderId: string;
  dateOfService?: string;
  providerName?: string;
  insuranceName?: string;
  hideButton?: boolean;
  orderData?: MedicalPaOrder;
  badgeList?: BadgeWithIconProps[];
  selectedBadge?: BadgeWithIconProps;
  setSelectedBadge?: (badgeId: string) => void;
  setShowCigNoteModal?: (show: boolean) => void;
}
const HeaderSingleOrder = (props: AuthHeaderProps) => {
  const navigate = useNavigate();

  const { singleMedicalPaOrder } = useSelector(
    (state: RootState) => state.singleMedicalPaOrder,
  );
  const jcode_icd = useMemo(
    () => formatJcodeIcd(singleMedicalPaOrder || ({} as MedicalPaOrder)),
    [singleMedicalPaOrder],
  );
  const { data: authStatusOptions } = useSelector(
    (state: RootState) => state.authStatusOptions,
  );
  const { healthCareFacilityData } = useHealthCareFacility(
    getOrgIdForFetchExternalWorklist() ?? "",
  );

  const { user } = useSelector(
    (state: RootState) => state.firebaseAuthentication,
  );
  const hasMissingConfig = useMemo(() => {
    if (!singleMedicalPaOrder?.auth_on_file?.auth_entries) {
      return false;
    }

    return singleMedicalPaOrder.auth_on_file.auth_entries.some(
      (entry) =>
        entry.j_codes &&
        Array.isArray(entry.j_codes) &&
        entry.j_codes.some(
          (j) => j.nccn_match === EventsName.NCCN_CONFIG_MISSING,
        ),
    );
  }, [singleMedicalPaOrder]);

  const hasNonPreferredDrug = useMemo(() => {
    if (!singleMedicalPaOrder?.auth_on_file?.auth_entries) {
      return false;
    }

    return singleMedicalPaOrder.auth_on_file.auth_entries.some(
      (entry) =>
        entry.j_codes &&
        Array.isArray(entry.j_codes) &&
        entry.j_codes.some(
          (j) => j.drug_preference === EventsName.DRUG_PREFERENCE,
        ),
    );
  }, [singleMedicalPaOrder]);

  const { payerName } = usePayerNameContextFromSingleOrderLayout();
  const { manualAuthStatus } = useBusniessOfficeNotesContext();

  const { openedModalId } = useSelector((state: RootState) => state.modal);
  const { globalBoValue } = useBusniessOfficeNotesContext();
  const logJCodeStatusEvents = useLogJCodeStatusEvents();
  const [isLoading, setIsLoading] = useState(false);
  const [items, setItems] = useState<any>([]);
  const [payers, setPayers] = useState<string[]>([]);
  const [loadingPayers, setLoadingPayers] = useState(false);
  const orgId = getOrgIdForFetchExternalWorklist();
  const { oldAuthStatus: oldAuthStatusFromContext } =
    useInitialDataOfSingleMedicalOrder();
  const dispatch = useDispatch<AppDispatch>();

  const { handleAuthOnFileRefresh, isRefreshing } = useAuthOnFileRefresh({
    orderId: props?.orderId ?? "",
    patientMrn: props?.orderData?.payload?.patient_id,
    drugName: props?.orderData?.payload?.regimen_name,
  });

  const getNccnGuideLinesForAnOrder = async () => {
    const item = await getNccnGuideLines({
      order_id: props.orderId ?? "",
    });
    setItems(item?.data as DocumentsViewerPropsWithSideNavigation[]);
  };

  useEffect(() => {
    if (props.orderId) {
      getNccnGuideLinesForAnOrder();
    }
  }, [props.orderId]);

  const getStatusBadge = useMemo(() => {
    if (props.status) {
      return authStatusOptions?.find(
        (option: BadgeWithIconProps) => option.id === props.status,
      );
    } else {
      return authStatusOptions?.find(
        (option: BadgeWithIconProps) =>
          option.id === MedicalPaOrdersAuthStatus.NotAvailable,
      );
    }
  }, [props.status]);

  useEffect(() => {
    if (openedModalId === ModalId.FORM_SELECTION_MODAL) {
      openModal(ModalId.FORM_SELECTION_MODAL);
    }
    if (openedModalId === ModalId.HAPPY_FLOW_MODAL) {
      openModal(ModalId.HAPPY_FLOW_MODAL);
    }
    if (openedModalId === ModalId.COMMENT_RPA_MODAL_NEW) {
      openModal(ModalId.COMMENT_RPA_MODAL_NEW);
    }
    if (openedModalId === ModalId.NAR_RPA_TRIGGER_MODAL) {
      openModal(ModalId.NAR_RPA_TRIGGER_MODAL);
    }
  }, [openedModalId]);

  const shouldShowThreeDots = useMemo(() => {
    if (singleMedicalPaOrder?.auth_on_file) {
      return true;
    }
    return false;
  }, [singleMedicalPaOrder]);

  const handleHappyFlowModalSave = async () => {
    try {
      apiWorkerManager.enqueueAndProcess({
        patient_id: singleMedicalPaOrder?.patient_id ?? "",
        order_id: singleMedicalPaOrder?.order_id ?? "",
        org_id: orgId ?? "",
        event_name: EventsName.MARK_AS_COMPLETED_BUTTON_SAVED,
        user_id: user?.id ?? "",
        user_email: user?.email ?? "",
        additional_data: {
          screen_name: ScreenNamesForBigQuery.AUTH_STATUS_SCREEN,
          modal_name: ModalNamesForBigQuery.HAPPY_FLOW_MODAL,
          master_auth_status_old: oldAuthStatusFromContext,
          master_auth_status_new:
            singleMedicalPaOrder?.auth_on_file?.auth_status ?? "",
          payer_name: singleMedicalPaOrder?.coverage?.primary?.payer_name ?? "",
          plan_name: singleMedicalPaOrder?.coverage?.primary?.plan_name ?? "",
          j_code: (
            getItemFromLocalStorage(
              `${LocalStorageKeys.J_CODES}_${singleMedicalPaOrder?.id}`,
            ) ?? []
          ).join(","),
        },
      });

      const allJCodes = getAllJCodes(singleMedicalPaOrder ?? {});
      if (allJCodes) {
        logJCodeStatusEvents(allJCodes, 1000);
      }

      setIsLoading(true);
      const payload = {
        identifier: singleMedicalPaOrder?.id ?? "",
        org_id: orgId,
        bo_status: globalBoValue,
        overwrite_rpa_errors: false,
        write_back_reports: null,
      };
      await commentRpaModalSave(payload);
      setIsLoading(false);
      dispatch(closeModal());
      controlToastState("mark-as-completed-success");
      navigate(
        `/worklists/${getItemFromLocalStorage(LocalStorageKeys.PARENT_TABLE)}`,
      );
    } catch (error) {
      controlToastState("mark-as-completed-error");
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    const getAllPayers = async () => {
      try {
        setLoadingPayers(true);
        const payersData = await FirestoreService.getDocument(
          FirestoreCollectionReference.payers("nar_payers"),
        );
        setPayers((payersData as any)?.data || []);
      } catch (error) {
        console.error("Error fetching payers:", error);
        setPayers([]);
      } finally {
        setLoadingPayers(false);
      }
    };
    getAllPayers();
  }, []);

  const shouldShowNarRpaTrigger = useMemo(() => {
    const currentPayerName = props.insuranceName;
    if (currentPayerName && payers.length > 0) {
      return payers.some((payer) =>
        payer.toLowerCase().includes(currentPayerName.toLowerCase()),
      );
    }
    return false;
  }, [singleMedicalPaOrder, payers]);

  const isNarRpaInProgress = useMemo(() => {
    return (
      singleMedicalPaOrder?.status?.health_first_nar_rpa_status ===
      NarRpsStatus.IN_PROGRESS
    );
  }, [singleMedicalPaOrder?.status?.health_first_nar_rpa_status]);

  const handleNarRpaTrigger = async () => {
    dispatch(
      setOpenedModalId({
        id: ModalId.NAR_RPA_TRIGGER_MODAL,
        metaData: { orderId: props.orderId },
      }),
    );
  };

  const hoverText = singleMedicalPaOrder?.metadata?.alerts?.join(", ") || "";
  const primaryIcdCodes = usePrimaryIcdCodes();

  return (
    <div className="authorization-layout--headerflex flex h-full w-full flex-row items-center justify-between border-b-2 border-dashed border-primaryGray-15 pb-3 pt-0">
      <div className="flex flex-col items-start gap-2">
        <div className="authorization-layout--header__left flex h-full flex-row items-center gap-2 px-1">
          <div className="authorization-layout--header__left--name flex flex-row items-center gap-1">
            <div className="text-h16 font-bold">{props.patientName}</div>
            <div>{`(${props.patientMrn})`}</div>
          </div>
          <div className="authorization-layout--header__left--status ml-4 flex flex-row gap-2">
            <BadgeWithIcon
              text={getStatusBadge?.text ?? ""}
              id={getStatusBadge?.id ?? ""}
              bgColor={getStatusBadge?.bgColor ?? ""}
              textColor={getStatusBadge?.textColor ?? ""}
            />
            {singleMedicalPaOrder?.auth_on_file?.flowsheet_regimen_details
              ?.regimen_tag &&
              singleMedicalPaOrder?.auth_on_file?.flowsheet_regimen_details
                ?.regimen_tag !== "" && (
                <BadgeWithIconRounded
                  text={
                    getBadgeDetailsAndIconColorForCareTag(
                      props.orderData?.auth_on_file?.flowsheet_regimen_details
                        ?.regimen_tag ?? "",
                    ).badgeText
                  }
                  icon={
                    <div
                      style={{
                        width: "10px",
                        height: "10px",
                        background: getBadgeDetailsAndIconColorForCareTag(
                          props.orderData?.auth_on_file
                            ?.flowsheet_regimen_details?.regimen_tag ?? "",
                        ).iconColor,
                        borderRadius: "9999px",
                      }}
                    ></div>
                  }
                  id="id2"
                  bgColor={
                    getBadgeDetailsAndIconColorForCareTag(
                      props.orderData?.auth_on_file?.flowsheet_regimen_details
                        ?.regimen_tag ?? "",
                    ).badgeBgColor
                  }
                  textColor={
                    getBadgeDetailsAndIconColorForCareTag(
                      props.orderData?.auth_on_file?.flowsheet_regimen_details
                        ?.regimen_tag ?? "",
                    ).badgeTextColor
                  }
                  borderColor={
                    getBadgeDetailsAndIconColorForCareTag(
                      props.orderData?.auth_on_file?.flowsheet_regimen_details
                        ?.regimen_tag ?? "",
                    ).borderColor
                  }
                />
              )}
            {props.orderId === "7d5b393e-83de-4380-b5e4-90c82acc4620" && (
              <div className="flex flex-row items-center gap-2 rounded-sm bg-red-50 px-2">
                <WarningIcon width={"12"} height={"12"} />
                <div className="text-xs text-red-500">Denied</div>
              </div>
            )}
            <div className="flex flex-wrap gap-1">
              {singleMedicalPaOrder?.metadata?.alert_badges?.map(
                (badge, index) => (
                  <TagWithIcon
                    key={index}
                    text={badge}
                    textOnHover={hoverText}
                    maxWidth="20rem"
                    placement="bottom"
                  />
                ),
              )}
            </div>
            <div className="flex gap-3">
              {hasNonPreferredDrug && (
                <BadgeWithIconRounded
                  text="Non-Preferred drugs"
                  id="non-preferred-drugs"
                  bgColor="#FAE6E6"
                  textColor="#1F1F1F"
                  borderColor="#E88B8B"
                  icon={<AlertTriangleIcon width={12} height={12} />}
                />
              )}
              {hasMissingConfig && (
                <BadgeWithIconRounded
                  text="Missing NCCN Preferred ICD-10 Code"
                  id="missing-nccn-config"
                  bgColor="#FAE6E6"
                  textColor="#1F1F1F"
                  borderColor="#E88B8B"
                  icon={<RedFlagIcon width={9.8} height={14} />}
                />
              )}
            </div>
          </div>
        </div>
        <AuthorizationDetails
          insuranceName={props.insuranceName}
          dateOfService={props.dateOfService}
          providerName={props.providerName}
          location={props.location}
          primaryIcdCode={primaryIcdCodes}
          orderId={props.orderId}
          onEditIcdClick={() => {
            apiWorkerManager.enqueueAndProcess({
              patient_id: singleMedicalPaOrder?.patient_id ?? "",
              order_id: singleMedicalPaOrder?.order_id ?? "",
              org_id: orgId ?? "",
              event_name: EventsName.PRIMARY_ICD_EDIT_BUTTON_CLICKED,
              user_id: user?.id ?? "",
              user_email: user?.email ?? "",
              additional_data: {
                screen_name: ScreenNamesForBigQuery.AUTH_STATUS_SCREEN,
                payer_name:
                  singleMedicalPaOrder?.coverage?.primary?.payer_name ?? "",
                assigned_primary_icd_code: primaryIcdCodes,
                j_code: (
                  getItemFromLocalStorage(
                    `${LocalStorageKeys.J_CODES}_${singleMedicalPaOrder?.id}`,
                  ) ?? []
                ).join(","),
                jcode_icd,
              },
            });
            handleEditIcd(dispatch, props.orderId);
          }}
        />
      </div>

      <>
        <div className="flex flex-row items-center gap-2 self-end">
          {loadingPayers && props.insuranceName && (
            <div className="mr-2 flex items-center">
              <SpinningLoader />
            </div>
          )}
          {!loadingPayers && shouldShowNarRpaTrigger && (
            <div
              className={`mr-2 text-sm font-semibold ${
                isNarRpaInProgress
                  ? "text-primaryGray-10 opacity-50"
                  : "cursor-pointer text-primaryGray-1 hover:text-primaryGray-5 hover:underline"
              }`}
              onClick={() => {
                if (isNarRpaInProgress) return;

                handleNarRpaTrigger();
                apiWorkerManager.enqueueAndProcess({
                  patient_id: singleMedicalPaOrder?.patient_id ?? "",
                  order_id: singleMedicalPaOrder?.order_id ?? "",
                  org_id: orgId ?? "",
                  event_name: EventsName.TRIGGER_RPA_CLICKED_NAR,
                  user_id: user?.id ?? "",
                  user_email: user?.email ?? "",
                  additional_data: {
                    screen_name: ScreenNamesForBigQuery.AUTH_STATUS_SCREEN,
                    payer_name: payerName,
                  },
                });
              }}
            >
              Trigger NAR RPA
            </div>
          )}
          {!props?.hideButton && (
            <div className="flex flex-row items-center gap-3">
              {shouldShowThreeDots && (
                <div className="ml-auto flex items-center gap-2">
                  <ThreeDotsWithClickableOptions
                    items={items}
                    healthCareFacilityData={healthCareFacilityData}
                  />
                </div>
              )}
              <div
                className="refresh-icon flex h-[1.375rem] w-[1.375rem] items-center justify-center rounded-[0.1375rem] bg-[#F7F9FA] hover:scale-105 hover:cursor-pointer"
                onClick={() => {
                  handleAuthOnFileRefresh();
                }}
              >
                {isRefreshing ? <SpinningLoader /> : <RefreshIcon />}
              </div>
            </div>
          )}
        </div>
      </>

      {openedModalId === ModalId.FORM_SELECTION_MODAL && <FormSelectionModal />}
      {openedModalId === ModalId.HAPPY_FLOW_MODAL && (
        <HappyFlowModal
          onClose={() => dispatch(closeModal())}
          onSave={() => {
            handleHappyFlowModalSave();
          }}
          isLoading={isLoading}
        />
      )}
      {openedModalId === ModalId.COMMENT_RPA_MODAL_NEW && (
        <CommentRpaNewModal
          onClose={() => dispatch(closeModal())}
          onSave={() => {
            apiWorkerManager.enqueueAndProcess({
              patient_id: singleMedicalPaOrder?.patient_id ?? "",
              order_id: singleMedicalPaOrder?.order_id ?? "",
              org_id: orgId ?? "",
              event_name: EventsName.MARK_AS_COMPLETED_BUTTON_SAVED,
              user_id: user?.id ?? "",
              user_email: user?.email ?? "",
              additional_data: {
                screen_name: ScreenNamesForBigQuery.AUTH_STATUS_SCREEN,
                modal_name: ModalNamesForBigQuery.COMMENT_RPA_MODAL_NEW,
                payer_name:
                  singleMedicalPaOrder?.coverage?.primary?.payer_name ?? "",
                master_auth_status_old: oldAuthStatusFromContext,
                master_auth_status_new:
                  singleMedicalPaOrder?.auth_on_file?.auth_status ?? "",
                plan_name:
                  singleMedicalPaOrder?.coverage?.primary?.plan_name ?? "",
                j_code: (
                  getItemFromLocalStorage(
                    `${LocalStorageKeys.J_CODES}_${singleMedicalPaOrder?.id}`,
                  ) ?? []
                ).join(","),
              },
            });

            const allJCodes = getAllJCodes(singleMedicalPaOrder ?? {});
            if (allJCodes) {
              logJCodeStatusEvents(allJCodes, 1000);
            }

            dispatch(closeModal());
            controlToastState("mark-as-completed-success");
            navigate(
              `/worklists/${getItemFromLocalStorage(LocalStorageKeys.PARENT_TABLE)}`,
            );
          }}
          wrtiteBackStatus={
            singleMedicalPaOrder?.status?.ev_write_back_status ?? ""
          }
          docRpaStatus={
            singleMedicalPaOrder?.status?.document_upload_status ?? ""
          }
          id={singleMedicalPaOrder?.id ?? ""}
          orderId={props.orderId}
          initialAuthStatus={
            singleMedicalPaOrder?.auth_on_file?.auth_status ?? ""
          }
          auth_status={manualAuthStatus ?? undefined}
        />
      )}
      {openedModalId === ModalId.NAR_RPA_TRIGGER_MODAL && (
        <NarRpaTriggerModal
          onClose={() => dispatch(closeModal())}
          metaData={{ orderId: props.orderId }}
        />
      )}
      {openedModalId === ModalId.CLINICAL_DATA_TABLE && (
        <ClinicalDataTable
          data={{
            entries: [],
          }}
          onSave={() => {
            controlToastState("icd-10-saved-successfully");
            dispatch(closeModal());
          }}
          onClose={() => dispatch(closeModal())}
        />
      )}
    </div>
  );
};

export default HeaderSingleOrder;
